import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { listCoupons, getCoupon, createCoupon, updateCoupon, deleteCoupon, ApiError, type CouponCampaignRow, type MailReward } from "@/lib/api";
import { cn } from "@/lib/utils";
import { getItemName } from "@/data/item-names";
import { IAP_CATALOG, IAP_REWARD_TYPE } from "@/lib/iap-rewards";
import { HERO_NAMES, REWARD_TYPE_NAMES } from "@/types/cloud-data";
import { ItemPicker } from "@/components/ItemPicker";
import { CurrencyIcon, HeroPortrait, ItemIcon } from "@/components/GameIcon";
import { ProductArt, productName } from "@/components/purchases/ProductArt";
import { WorkspaceShell, usePreviewReadOnly } from "@/components/workspace/WorkspaceShell";
import "./game-config.css";

type EditorState = { mode: "new" } | { mode: "edit"; code: string };
type CouponReward = MailReward;

// Keep this in lockstep with Send Mail: these fields mirror GameManager.GetReward.
const fields: Record<number, { amount?: boolean; item?: boolean; hero?: boolean; buff?: boolean; recipe?: boolean; iap?: boolean }> = {
  0: { amount: true, item: true }, 1: { amount: true }, 2: { amount: true },
  4: { hero: true }, 10: { amount: true, buff: true }, 11: { amount: true },
  12: { amount: true }, 14: { amount: true }, 15: { amount: true }, 16: { recipe: true }, 100: { amount: true, iap: true },
};
const DEFAULT_REWARD: CouponReward = { rewardType: 2, amount: 100 };
const iapNames = IAP_CATALOG.map(p => productName(p.id));
const iapLabel = (id: string, i: number) => iapNames.indexOf(iapNames[i]) !== iapNames.lastIndexOf(iapNames[i]) ? `${iapNames[i]} · ${id}` : iapNames[i];
const findIap = (id?: string) => IAP_CATALOG.find(p => p.id === id);

function rewardLabel(reward: Partial<CouponReward>): string {
  if (reward.rewardType === 0) return getItemName(reward.itemID) || "Item";
  if (reward.rewardType === 4) return HERO_NAMES[reward.hero ?? 0] ?? "Hero";
  if (reward.rewardType === IAP_REWARD_TYPE && reward.iapProductId) return productName(reward.iapProductId);
  return typeof reward.rewardType === "number" ? REWARD_TYPE_NAMES[reward.rewardType] ?? `Type ${reward.rewardType}` : "Unknown";
}

function CouponRewards({ value }: { value: unknown }) {
  if (!Array.isArray(value) || value.length === 0) return <span className="ws-muted">No rewards</span>;
  return <ul className="gc-chips">{(value as CouponReward[]).map((reward, i) => {
    // Gold and ruby amounts run into the millions; group their digits.
    const amount = reward.rewardType === 1 || reward.rewardType === 2 ? reward.amount?.toLocaleString("en-US") : reward.amount;
    const text = `${rewardLabel(reward)}${amount !== undefined && reward.rewardType !== 4 ? ` ×${amount}` : ""}`;
    return <li key={i} className="gc-chip" title={text}><RewardIcon reward={reward} /><span>{text}</span></li>;
  })}</ul>;
}

function parseRecipients(raw: string): string[] {
  return [...new Set(raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean))];
}

function RewardIcon({ reward }: { reward: CouponReward }) {
  if (reward.rewardType === IAP_REWARD_TYPE && reward.iapProductId) return <ProductArt productId={reward.iapProductId} className="gc-art" />;
  if (reward.rewardType === 1) return <CurrencyIcon currency="gold" />;
  if (reward.rewardType === 2) return <CurrencyIcon currency="ruby" />;
  if (reward.rewardType === 4 && typeof reward.hero === "number") return <HeroPortrait heroId={reward.hero} />;
  return <ItemIcon id={reward.itemID} />;
}

/** Hint for an incomplete reward. Shown only; saving is not blocked (as before). */
function rewardProblem(reward: CouponReward) {
  const f = fields[reward.rewardType] ?? {};
  if (f.item && !reward.itemID) return "Pick an item";
  if (f.hero && reward.hero == null) return "Pick a hero";
  if (f.buff && !reward.developerBuffID?.trim()) return "Enter a buff ID";
  if (f.iap && !reward.iapProductId) return "Pick a product";
  return null;
}

function RewardRow({ reward, index, onChange, onRemove }: { reward: CouponReward; index: number; onChange: (patch: Partial<CouponReward>) => void; onRemove: () => void }) {
  const f = fields[reward.rewardType] ?? {};
  const problem = rewardProblem(reward);
  const n = index + 1;
  const product = findIap(reward.iapProductId);
  return <li className="gc-reward">
    <span className="gc-reward-icon"><RewardIcon reward={reward} /></span>
    <select className="gc-type" aria-label={`Reward ${n} type`} value={reward.rewardType} onChange={e => onChange({
      rewardType: parseInt(e.target.value), amount: 1, itemID: undefined, hero: undefined, developerBuffID: undefined, recipe: undefined, iapProductId: undefined })}>
      {Object.entries(REWARD_TYPE_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
    <span className="gc-detail">
      {f.item && <ItemPicker className="gc-item-picker" value={reward.itemID ?? ""} onChange={itemID => onChange({ itemID })} placeholder="Search items" />}
      {f.hero && <select aria-label={`Reward ${n} hero`} value={reward.hero ?? ""} onChange={e => onChange({ hero: parseInt(e.target.value) })}>
        <option value="" disabled>Hero</option>
        {Object.entries(HERO_NAMES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>}
      {f.buff && <input aria-label={`Reward ${n} buff ID`} placeholder="Buff ID" value={reward.developerBuffID ?? ""} onChange={e => onChange({ developerBuffID: e.target.value })} />}
      {f.recipe && <input aria-label={`Reward ${n} recipe ID`} placeholder="Recipe ID" type="number" value={reward.recipe ?? ""} onChange={e => onChange({ recipe: parseInt(e.target.value) || 0 })} />}
      {f.iap && <select required aria-label={`Reward ${n} IAP product`} value={reward.iapProductId ?? ""} title={product?.summary} onChange={e => onChange({ iapProductId: e.target.value })}>
        <option value="" disabled>IAP product</option>
        {reward.iapProductId && !product && <option value={reward.iapProductId} disabled>{reward.iapProductId} — unknown product</option>}
        {IAP_CATALOG.map((p, i) => <option key={p.id} value={p.id} disabled={!!p.unavailable}>{iapLabel(p.id, i)}{p.unavailable ? ` — unavailable: ${p.unavailable}` : ""}</option>)}
      </select>}
    </span>
    {f.amount ? <input className="gc-amount" aria-label={`Reward ${n} ${f.iap ? "packs" : "amount"}`} title={f.iap ? "Packs; expanded into rewards when saved" : undefined} type="number"
      value={reward.amount} onChange={e => onChange({ amount: parseInt(e.target.value) || 0 })} /> : <span className="gc-amount-spacer" />}
    <button type="button" className="pw-icon-button gc-remove" aria-label={`Remove reward ${n}`} onClick={onRemove}><X size={15} /></button>
    {(problem || (f.iap && product)) && <span className="gc-problem">{problem ?? `${product!.summary} · amount = packs`}</span>}
  </li>;
}

export function CouponsWorkspace() {
  const readOnly = usePreviewReadOnly();
  const qc = useQueryClient();
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const deleteDialog = useRef<HTMLDialogElement>(null);
  const query = useQuery({ queryKey: ["coupons"], queryFn: listCoupons });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["coupons"] });

  const toggleActive = useMutation({
    mutationFn: async (row: CouponCampaignRow) => {
      // Recipients live outside the stored campaign JSON (campaign.emails is always []), so a toggle must fetch the
      // real recipient list first — omitting `recipients` would fall back to campaign.emails ([]) and wipe targeting.
      const detail = await getCoupon(row.code);
      await updateCoupon(row.code, { campaign: detail.campaign, recipients: detail.recipients, active: !row.active });
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (code: string) => deleteCoupon(code),
    onSuccess: () => { setConfirmDelete(null); invalidate(); },
  });
  useEffect(() => {
    const dialog = deleteDialog.current;
    if (confirmDelete && !dialog?.open) dialog?.showModal();
    if (!confirmDelete && dialog?.open) dialog.close();
  }, [confirmDelete]);
  const closeDelete = () => { if (!remove.isPending) { setConfirmDelete(null); remove.reset(); } };

  const campaigns = query.data?.campaigns ?? [];

  return <WorkspaceShell crumbs={[{ label: "Game configuration" }, { label: "Coupons" }]} readOnly={readOnly}>
    <div className="ws-scroll"><div className="ws-page">
      <div className="ws-head"><h1>Coupons</h1>
        <div className="ws-head-actions"><button className="pw-button pw-button-primary" onClick={() => setEditor({ mode: "new" })} disabled={editor !== null}><Plus size={14} />New coupon</button></div>
      </div>
      {editor && <CouponEditor key={editor.mode === "edit" ? editor.code : "new"} state={editor} readOnly={readOnly} existingCodes={campaigns.map((c) => c.code)}
        onClose={() => setEditor(null)} onSaved={() => { setEditor(null); invalidate(); }} />}
      {toggleActive.error && <p className="ws-alert" role="alert">{String(toggleActive.error)}</p>}
      <section className="ws-card is-flush" aria-labelledby="gc-campaigns">
        <div className="ws-card-head"><h2 id="gc-campaigns">Campaigns</h2>{query.isSuccess && <span className="ws-muted">{campaigns.length}</span>}</div>
        {query.isLoading ? <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading campaigns…</div> : query.isError ? (
          <div className="pw-error" role="alert"><strong>Couldn’t load campaigns</strong><p>{String(query.error)}</p><button className="pw-button" onClick={() => void query.refetch()}>Try again</button></div>
        ) : campaigns.length === 0 ? <p className="ws-empty">No coupon campaigns yet</p> : <div className="ws-table" role="table" aria-label="Coupon campaigns">
          <div role="row" className="ws-table-row ws-table-head gc-coupon-row">{["Code", "Status", "Recipients", "Rewards"].map(text => <span role="columnheader" key={text}>{text}</span>)}<span role="columnheader" aria-label="Actions" /></div>
          {campaigns.map((row) => {
            const status = <button type="button" className={cn("pw-badge", row.active && "gc-badge-ok")} onClick={() => toggleActive.mutate(row)} disabled={toggleActive.isPending || readOnly}
              title={row.active ? "Active — click to deactivate" : "Inactive — click to activate"}>{row.active ? "Active" : "Inactive"}</button>;
            const recipients = row.recipient_count === 0 ? "Everyone" : `${row.recipient_count}`;
            return <div role="row" key={row.code} className="ws-table-row gc-coupon-row">
              <span role="cell" className="ws-mono" title={row.code}>{row.code}</span>
              <span role="cell">{status}</span>
              <span role="cell" className="ws-muted">{recipients}</span>
              <span role="cell"><CouponRewards value={row.campaign.rewardDatas} /></span>
              <span className="gc-coupon-meta">{status}<span className="ws-muted">{row.recipient_count === 0 ? "Everyone" : `${row.recipient_count} recipients`}</span></span>
              <span role="cell" className="gc-row-actions">
                <button className="pw-icon-button" aria-label={`Edit ${row.code}`} title="Edit" onClick={() => setEditor({ mode: "edit", code: row.code })}><Pencil size={14} /></button>
                <button className="pw-icon-button gc-remove" aria-label={`Delete ${row.code}`} title="Delete" onClick={() => setConfirmDelete(row.code)}><Trash2 size={14} /></button>
              </span>
            </div>;
          })}
        </div>}
      </section>
      <dialog ref={deleteDialog} className="ws-dialog" aria-labelledby="gc-delete-title" onCancel={e => { e.preventDefault(); closeDelete(); }}>
        <div className="ws-dialog-head"><h2 id="gc-delete-title">Delete campaign</h2><button className="pw-icon-button" aria-label="Close" onClick={closeDelete} disabled={remove.isPending}><X size={16} /></button></div>
        <div className="ws-dialog-body"><p>Delete <span className="ws-mono">{confirmDelete}</span> and its recipient list? This can’t be undone.</p>
          {remove.error && <p className="ws-alert" role="alert">{String(remove.error)}</p>}</div>
        <div className="ws-dialog-foot"><button className="pw-button" onClick={closeDelete} disabled={remove.isPending}>Cancel</button>
          <button className="pw-button pw-button-danger" onClick={() => confirmDelete && remove.mutate(confirmDelete)} disabled={remove.isPending || readOnly}>{remove.isPending ? "Deleting…" : "Delete"}</button></div>
      </dialog>
    </div></div>
  </WorkspaceShell>;
}

function CouponEditor({ state, existingCodes, readOnly, onClose, onSaved }: { state: EditorState; existingCodes: string[]; readOnly: boolean; onClose: () => void; onSaved: () => void }) {
  const isEdit = state.mode === "edit";
  const [code, setCode] = useState(isEdit ? state.code : "");
  const [active, setActive] = useState(true);
  const [recipientsText, setRecipientsText] = useState("");
  const [rewards, setRewards] = useState<CouponReward[]>([]);
  const [loaded, setLoaded] = useState(!isEdit);
  const detail = useQuery({
    queryKey: ["coupon", isEdit ? state.code : null],
    queryFn: () => getCoupon((state as { mode: "edit"; code: string }).code),
    enabled: isEdit,
  });
  useEffect(() => {
    if (isEdit && !loaded && detail.isSuccess && detail.data) {
      setActive(!!detail.data.active);
      setRecipientsText(detail.data.recipients.join("\n"));
      setRewards(Array.isArray(detail.data.campaign.rewardDatas) ? detail.data.campaign.rewardDatas as CouponReward[] : []);
      setLoaded(true);
    }
  }, [isEdit, loaded, detail.isSuccess, detail.data]);
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedCode = code.trim();
      if (!trimmedCode) throw new Error("Code is required.");
      if (!isEdit && existingCodes.includes(trimmedCode)) throw new Error("A campaign with this code already exists.");
      const campaign = { code: trimmedCode, emails: [] as string[], rewardDatas: rewards };
      const recipients = parseRecipients(recipientsText);
      if (isEdit) await updateCoupon(trimmedCode, { campaign, recipients, active });
      else await createCoupon({ code: trimmedCode, campaign, recipients, active });
    },
    onError: (e) => setFormError(e instanceof ApiError ? e.message : String((e as Error).message ?? e)),
    onSuccess: onSaved,
  });
  function handleSubmit(e: React.FormEvent) { e.preventDefault(); setFormError(null); save.mutate(); }
  const busy = save.isPending || (isEdit && detail.isLoading) || readOnly;
  const updateReward = (index: number) => (patch: Partial<CouponReward>) => setRewards((current) => current.map((reward, i) => i === index ? { ...reward, ...patch } : reward));
  const recipientCount = parseRecipients(recipientsText).length;

  return <form onSubmit={handleSubmit} className="ws-card ws-form" aria-label={isEdit ? `Edit ${state.code}` : "New campaign"}>
    <div className="ws-card-head gc-editor-head"><h2>{isEdit ? <>Edit <span className="ws-mono">{state.code}</span></> : "New campaign"}</h2>
      <button type="button" onClick={() => setActive((a) => !a)} className={cn("pw-badge", active && "gc-badge-ok")} title="Toggle active">{active ? "Active" : "Inactive"}</button>
      <button type="button" className="pw-icon-button" aria-label="Close editor" onClick={onClose} disabled={save.isPending}><X size={15} /></button></div>
    {isEdit && detail.isLoading ? <div className="pw-empty" role="status"><span className="pw-loading-dot" />Loading campaign…</div> : isEdit && detail.isError ? (
      <p className="ws-alert" role="alert">Failed to load campaign: {String(detail.error)}</p>
    ) : <>
      <input className="gc-code gc-mono" aria-label="Code" value={code} onChange={(e) => setCode(e.target.value)} disabled={isEdit} placeholder="Code, e.g. LAUNCH_WEEK_2026" />
      <textarea className="gc-mono" aria-label="Recipient UIDs" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} rows={4} spellCheck={false}
        placeholder={"Recipient UIDs, one per line or comma separated\nLeave empty to let every player claim this coupon"} />
      {recipientCount > 0 && <span className="gc-hint">{recipientCount} recipient{recipientCount === 1 ? "" : "s"} · never shown to players</span>}
      <h2 className="gc-subhead">Rewards</h2>
      {rewards.length ? <ol className="gc-rewards">{rewards.map((reward, index) => <RewardRow key={index} index={index} reward={reward} onChange={updateReward(index)}
        onRemove={() => setRewards((current) => current.filter((_, i) => i !== index))} />)}</ol> : <p className="gc-none">No rewards</p>}
      <button type="button" className="pw-button gc-add" onClick={() => setRewards((current) => [...current, { ...DEFAULT_REWARD }])}><Plus size={14} />Add reward</button>
      {formError && <p className="ws-alert" role="alert">{formError}</p>}
      <div className="gc-actions is-end">
        <button type="button" className="pw-button" onClick={onClose} disabled={save.isPending}>Cancel</button>
        <button type="submit" className="pw-button pw-button-primary" disabled={busy}>{save.isPending ? "Saving…" : isEdit ? "Save changes" : "Create campaign"}</button>
      </div>
    </>}
  </form>;
}
